import {
  type CreateAdminRepresentativeDto,
  type UpdateAdminRepresentativeDto,
  type V2AdminRepresentativeDto,
  type V2AdminRepresentativesPageDto,
  type V2AdminRepresentativesSummaryDto,
  v2AdminRepresentativesControllerCreate,
  v2AdminRepresentativesControllerDetail,
  v2AdminRepresentativesControllerList,
  v2AdminRepresentativesControllerRemove,
  v2AdminRepresentativesControllerSummary,
  v2AdminRepresentativesControllerUpdate
} from '@idc-forms/api-client'
import {
  V2AdminRepresentativeSchema,
  V2AdminRepresentativesPageSchema,
  V2AdminRepresentativesSummarySchema
} from '@idc-forms/schemas/api-v2/admin-representatives'
import { assertShape } from '@/shared/api/assert-shape'
import { bearer, v2RequestOptions } from '@/shared/api/v2-request'

/**
 * Administración de representantes legales contra la API v2 (ADR 0019/0020).
 * Consume el SDK generado con el Bearer de la sesión staff v2, pagina por cursor y
 * usa el contrato de consulta rico. Identifica todo por id público (`rep_`); la
 * sociedad viaja por `soc_` y la autoría por `usr_`, nunca como ObjectId.
 *
 * `v2RequestOptions` es OBLIGATORIO en cada llamada: trae el `querySerializer` con
 * bracket notation. El serializer por defecto del SDK usa `deepObject` de UN solo
 * nivel y explota en runtime ante cualquier filtro anidado del ADR 0020.
 */

/** Parámetros amigables del listado; el servicio los mapea al query del SDK. */
export type RepresentativesAdminV2Query = {
  filters?: Record<string, unknown>
  /** `campo:asc|desc` (un solo campo; el cursor desempata por id). */
  sort?: string
  search?: string
  pageSize?: number
  after?: string
  before?: string
}

export type RepresentativesAdminV2Row = V2AdminRepresentativesPageDto['data'][number]

function toListQuery(query: RepresentativesAdminV2Query) {
  return {
    ...(query.filters && Object.keys(query.filters).length ? { filters: query.filters } : {}),
    ...(query.sort ? { sort: query.sort } : {}),
    ...(query.search ? { search: query.search } : {}),
    ...(query.pageSize ? { 'page[size]': query.pageSize } : {}),
    ...(query.after ? { 'page[after]': query.after } : {}),
    ...(query.before ? { 'page[before]': query.before } : {})
  }
}

export const representativesAdminV2Service = {
  list: async (
    accessToken: string,
    query: RepresentativesAdminV2Query
  ): Promise<V2AdminRepresentativesPageDto> => {
    const result = await v2AdminRepresentativesControllerList({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      query: toListQuery(query)
    })
    return assertShape(V2AdminRepresentativesPageSchema, result.data)
  },

  summary: async (
    accessToken: string,
    query: Pick<RepresentativesAdminV2Query, 'filters' | 'search'>
  ): Promise<V2AdminRepresentativesSummaryDto> => {
    const result = await v2AdminRepresentativesControllerSummary({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      query: toListQuery(query)
    })
    return assertShape(V2AdminRepresentativesSummarySchema, result.data)
  },

  detail: async (accessToken: string, id: string): Promise<V2AdminRepresentativeDto> => {
    const result = await v2AdminRepresentativesControllerDetail({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id }
    })
    return assertShape(V2AdminRepresentativeSchema, result.data)
  },

  create: async (
    accessToken: string,
    body: CreateAdminRepresentativeDto
  ): Promise<V2AdminRepresentativeDto> => {
    const result = await v2AdminRepresentativesControllerCreate({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      body
    })
    return result.data
  },

  update: async (
    accessToken: string,
    id: string,
    body: UpdateAdminRepresentativeDto
  ): Promise<V2AdminRepresentativeDto> => {
    const result = await v2AdminRepresentativesControllerUpdate({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id },
      body
    })
    return result.data
  },

  remove: async (accessToken: string, id: string): Promise<void> => {
    await v2AdminRepresentativesControllerRemove({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id }
    })
  }
}
